import { BitmapFont } from 'pixi.js';
import { INVITE_TTL_MS, type ErrorMsg, type InviteView } from '@shared/protocol/messages';
import { GameLoop } from '../game/core/GameLoop';
import { PixiHost } from '../game/core/PixiHost';
import { SceneManager, type SceneName } from '../game/core/SceneManager';
import { Viewport } from '../game/core/Viewport';
import { DebugOverlay } from '../game/core/DebugOverlay';
import { Tweens } from '../game/animation/Tweens';
import { motion } from '../game/animation/Motion';
import { AssetCache } from '../game/rendering/AssetCache';
import { Sfx } from '../assets/Sfx';
import { Music } from '../assets/Music';
import { GameStore } from './GameStore';
import { ApiClient } from '../networking/ApiClient';
import { MultiplayerClient } from '../networking/MultiplayerClient';
import { RoomSession } from '../networking/RoomSession';
import { ChatSession } from '../networking/ChatSession';
import { CloudSync } from '../networking/cloud';
import { welcomeSignIn } from '../ui/account';
import { ensureConsent } from '../ui/consent';
import { watchForUpdates } from './updates';
import { setupPhoneFullscreen } from './fullscreen';
import { handlePurchaseReturn, syncEntitlements } from '../networking/shop';
import { MenuScene } from '../game/scenes/MenuScene';
import { NotebookScene } from '../game/scenes/NotebookScene';
import { BattleScene, type BattleParams } from '../game/scenes/BattleScene';
import { forfeitUnfinished } from '../game/systems/progression';
import { INJURY_MINUTES, injuredFor, restMinutes } from '../game/systems/injury';
import { CreatorScene } from '../game/scenes/CreatorScene';
import { HelpScene } from '../game/scenes/HelpScene';
import { MultiplayerScene } from '../game/scenes/MultiplayerScene';
import { DrawingTextures } from '../drawing/DrawingTextures';
import { ensureOnline, goLive } from '../networking/session';
import { button } from '../ui/components';
import { h } from '../ui/dom';
import { BITMAP_FONT } from '../game/rendering/fonts';
import { ASSET_CATALOG } from '../assets/catalog';
import { resolveLanguage, setLanguage, t } from '../i18n';
import { registerIconArt } from '../ui/icons';
import { askAge, syncKidMode } from '../ui/family';
import { REACTION_INFO } from '../game/systems/community';
import { QuickFight, takeRoomFromUrl } from '../networking/quickFight';
import { refreshDares } from '../networking/community';
import { AdaptiveQuality } from '../game/core/AdaptiveQuality';
import { lastScene } from './resume';

export { BITMAP_FONT };

/** Composition root: creates every long-lived service exactly once. */
export class App {
  readonly viewport = new Viewport();
  readonly pixi = new PixiHost();
  readonly tweens = new Tweens();
  readonly assets = new AssetCache();
  readonly drawings = new DrawingTextures(this.assets);
  readonly sfx = new Sfx();
  /** Background music: the toy piano on calm pages, the sneaky boss in battles. */
  readonly music = new Music();
  readonly store: GameStore;
  readonly api = new ApiClient();
  readonly mp: MultiplayerClient;
  readonly room: RoomSession;
  /** Starts a battle by itself once a friend is in my quick room. */
  readonly quick: QuickFight;
  /** World chat (13+). */
  readonly chat: ChatSession;
  /** Cloud copy of the notebook while signed in. */
  readonly cloud: CloudSync;
  readonly scenes: SceneManager;
  readonly loop: GameLoop;
  readonly debug: DebugOverlay;
  /** Dynamic resolution (started with the loop). */
  quality: AdaptiveQuality | null = null;
  /** The battle invite this player sent and is waiting on, if any. */
  outgoingInvite: { id: string; name: string; until: number } | null = null;
  /** Attempts already announced per sent dare. */
  private readonly dareAttempts = new Map<string, number>();
  private readonly overlay: HTMLElement;

  constructor() {
    const ui = document.getElementById('ui-layer');
    const overlay = document.getElementById('overlay-layer');
    if (!ui || !overlay) throw new Error('missing layers');
    this.overlay = overlay;
    this.store = new GameStore(window.localStorage, (msg) => this.toast(msg, 'error'));
    // Language is fixed for the page's lifetime; changing it in Settings reloads.
    setLanguage(resolveLanguage(this.store.state.settings.language));
    this.mp = new MultiplayerClient(this.api);
    this.room = new RoomSession(this.mp);
    this.quick = new QuickFight(this);
    this.chat = new ChatSession(this.mp);
    this.cloud = new CloudSync(this);
    // Online battles can start while the player is on any screen.
    this.room.on('battleStart', () => {
      if (this.scenes.currentName !== 'battle') void this.scenes.go('battle', { mode: 'pvp' }, 'page');
    });
    this.room.on('battleSync', () => {
      if (this.scenes.currentName !== 'battle') void this.scenes.go('battle', { mode: 'pvp' }, 'fade');
    });
    // What the server pushes makes cached answers stale: forget them first, before anything re-reads.
    for (const ev of ['friend.request', 'friend.accepted', 'friend.removed', 'friend.online'] as const) this.mp.on(ev, () => this.api.forget('friends'));
    for (const ev of ['dare.received', 'dare.removed', 'dare.answered'] as const) this.mp.on(ev, () => this.api.forget('dares'));
    this.mp.on('fan.mail', () => this.api.forget('fanmail'));
    this.mp.on('replaced', (msg) => this.toast(msg, 'info'));
    this.room.on('challenge', (m) => {
      if (this.scenes.currentName !== 'multiplayer') this.toast(t('{name} challenged you! Open Battle With Friends.', { name: m.fromName }), 'info');
    });
    // Live friend events arrive on any screen while the socket is open.
    this.mp.on('friend.request', (m) => {
      this.sfx.play('pop');
      this.toast(t('{name} wants to be your friend! See Notebook → Friends.', { name: m.friend.name }), 'info');
    });
    this.mp.on('friend.accepted', (m) => this.toast(t('You and {name} are now friends!', { name: m.friend.name }), 'success'));
    this.mp.on('invite.incoming', (m) => this.showInvite(m.invite));
    // Dares and fan mail: keep the notebook's dare pages current and say so.
    this.mp.on('dare.received', (m) => {
      this.sfx.play('pop');
      this.toast(t('{name} dared you with {monster}! See Notebook → Friends.', { name: m.dare.fromName, monster: m.dare.monster.name }), 'info');
      void refreshDares(this);
    });
    this.mp.on('dare.removed', () => void refreshDares(this));
    this.mp.on('dare.answered', (m) => {
      const d = m.dare;
      // The same push follows a sticker; only a new attempt gets this toast (fan.mail covers stickers).
      if ((this.dareAttempts.get(d.id) ?? -1) >= d.attempts) return;
      this.dareAttempts.set(d.id, d.attempts);
      this.toast(
        d.status === 'beaten' ? t('{name} beat your dare {monster}!', { name: d.toName, monster: d.monster.name }) : t('{name} tried your dare {monster} and lost!', { name: d.toName, monster: d.monster.name }),
        'info',
      );
    });
    this.mp.on('fan.mail', (m) => {
      const n = m.note;
      if (!n.reaction) return;
      this.toast(t('{name} sent a "{sticker}" sticker for {monster}!', { name: n.fromName, sticker: t(REACTION_INFO[n.reaction].label), monster: n.monsterName }), 'success');
    });
    this.mp.on('invite.declined', (m) => {
      const inv = this.outgoingInvite;
      if (inv?.id !== m.byId) return;
      this.outgoingInvite = null;
      this.toast(t("{name} can't battle right now. Maybe next recess!", { name: inv.name }), 'info');
    });
    this.scenes = new SceneManager(this, ui, overlay);
    this.loop = new GameLoop(
      (dt) => {
        this.tweens.update(dt);
        this.scenes.update(dt);
      },
      () => this.pixi.render(),
    );
    this.debug = new DebugOverlay(this);
  }

  /** Tells the player that leaving a battle half-way was counted as a loss. */
  private reportFled(f: NonNullable<ReturnType<typeof forfeitUnfinished>>): void {
    const lost = f.coinsLost > 0 ? ` ${t('Dropped {n} coins.', { n: f.coinsLost })}` : '';
    const hurt = f.injured ? ` ${t('{name} is hurt and must rest for {n} min.', { name: f.hero, n: INJURY_MINUTES })}` : '';
    this.toast(`${t('You left the battle against {monster} half-way: it counts as a loss.', { monster: f.monster })}${lost}${hurt}`, 'error');
  }

  async start(): Promise<void> {
    await Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 2500))]);
    // Make sure the handwritten faces are actually loaded before baking them into textures.
    await Promise.allSettled(['32px "Gochi Hand"', '32px "Patrick Hand"', '32px Itim'].map((f) => document.fonts.load(f)));

    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    this.viewport.setDprCap(this.store.state.settings.dprCap);
    await this.pixi.init(canvas, this.viewport.info, this.store.state.settings.renderMode);
    document.documentElement.dataset.renderer = this.pixi.caps.renderer;
    const bootText = document.querySelector('.boot-text');
    await this.assets.loadManifest(`${import.meta.env.BASE_URL}assets/manifest.json`, (done, total) => {
      if (bootText && total) bootText.textContent = t('Sharpening pencils… {pct}%', { pct: Math.round((done / total) * 100) });
    });
    this.assets.buildAtlas(this.pixi.caps.maxTextureSize);
    this.exposeDomAssets();
    BitmapFont.install({
      name: BITMAP_FONT,
      style: { fontFamily: 'Gochi Hand, Patrick Hand, cursive', fontSize: 64, fill: '#ffffff', stroke: { color: '#3b3b3b', width: 8, join: 'round' } },
      chars: [['0', '9'], ['A', 'Z'], ['a', 'z'], ' +-×!?%.'],
      resolution: Math.min(2, this.viewport.info.dpr),
    });

    this.scenes.register('menu', (app) => new MenuScene(app));
    this.scenes.register('notebook', (app) => new NotebookScene(app));
    this.scenes.register('battle', (app) => new BattleScene(app));
    this.scenes.register('creator', (app) => new CreatorScene(app));
    this.scenes.register('help', (app) => new HelpScene(app));
    this.scenes.register('multiplayer', (app) => new MultiplayerScene(app));
    // A resting hero sits out story and monster battles; another hero can fight meanwhile.
    this.scenes.guard = (name, params) => {
      if (name !== 'battle' || (params as BattleParams).mode !== 'pve') return true;
      const hero = this.store.hero;
      if (!hero || injuredFor(hero) === 0) return true;
      this.toast(t('{name} is hurt and resting for {n} more min. Play another hero meanwhile!', { name: hero.name, n: restMinutes(hero) }), 'info');
      return false;
    };

    this.viewport.on('resize', (vp) => {
      this.pixi.resize(vp);
      this.scenes.layout(vp);
    });

    this.applySettings();
    this.store.subscribe(['settings'], () => this.applySettings());

    this.loop.start();
    // Dynamic resolution: drops the canvas resolution (then paper effects) while frames are slow.
    // Judged against the frame rate actually asked for (calm pages run at 30 on purpose).
    this.quality = new AdaptiveQuality(this.loop, this.pixi, () => (this.scenes.currentName === 'battle' ? this.store.state.settings.fpsCap : 30));
    document.getElementById('boot')?.remove();
    // A battle still running when the page was reloaded or closed counts as lost.
    let fled: ReturnType<typeof forfeitUnfinished> = null;
    if (this.store.state.battleInProgress) this.store.update(['hero', 'inventory', 'memories'], (s) => (fled = forfeitUnfinished(s)));
    // A shared room link (?room=ABC123) opens Battle With Friends and joins that room.
    const roomCode = takeRoomFromUrl();
    const resume = roomCode ? null : this.resumePoint();
    if (roomCode) await this.scenes.go('multiplayer', { joinCode: roomCode }, 'none');
    else if (resume) await this.scenes.go(resume.name as SceneName, resume.params as never, 'none');
    else await this.scenes.go(this.initialRoute(), undefined, 'none');
    if (this.store.loadedFrom === 'backup') this.toast(t('Your notebook was restored from its backup copy.'), 'info');
    if (fled) this.reportFled(fled);
    // Asked once, before anything online: kid mode depends on the answer.
    // A new player starts on the sign-in page (skipped offline). Google may
    // bring back a whole notebook, age included; otherwise the age is asked next.
    // The game agreement (terms, privacy, cookies) comes before everything else.
    await ensureConsent(this);
    const signIn = this.store.loadedFrom === 'new' ? await welcomeSignIn(this) : 'close';
    if (this.store.state.ageGroup === null) {
      await askAge(this);
      await syncKidMode(this);
    }
    // A guest account is made once the age is known: kid mode gets a generated name.
    if (signIn === 'guest') await ensureOnline(this, true);
    void goLive(this);
    // Purchases from other devices, and the way back from Stripe's payment page.
    void this.cloud.start().then(() => syncEntitlements(this)).then(() => handlePurchaseReturn(this));
    watchForUpdates(this);
    setupPhoneFullscreen(this);

    window.addEventListener('hashchange', () => {
      const name = location.hash.replace('#/', '') as SceneName;
      if (name !== this.scenes.currentName && ['menu', 'notebook', 'creator', 'help', 'multiplayer'].includes(name)) {
        void this.scenes.go(name, undefined);
      }
    });
  }

  /**
   * Delivered DOM art becomes CSS variables (--a-ui-hp-frame: url(...)) plus a
   * `has-ui-hp-frame` class on <html>, so stylesheets fall back gracefully
   * while an image is not delivered yet.
   */
  private exposeDomAssets(): void {
    const root = document.documentElement;
    for (const spec of ASSET_CATALOG) {
        // Skin readiness is gated on successful WebP decode by ui/skin.ts.
        if (spec.target !== 'dom' || spec.id.startsWith('skin:')) continue;
      const url = this.assets.url(spec.id, Object.keys(spec.states)[0]);
      if (!url) continue;
      const name = spec.id.replace(/[^a-z0-9]+/gi, '-');
      root.style.setProperty(`--a-${name}`, `url("${url}")`);
      root.classList.add(`has-${name}`);
      // Hand-drawn icons replace the inline SVG placeholders wherever icon() is used.
      if (spec.group === 'icons') registerIconArt(spec.id.slice('icon:'.length), url);
    }
  }

  /** After a reload (or an update) this tab reopens the page it was on, with its details (e.g. hero mode). */
  private resumePoint(): { name: SceneName; params: unknown } | null {
    const last = lastScene();
    if (!last) return null;
    const hash = location.hash.replace('#/', '');
    // The address bar wins when it names another page (a link or a typed route).
    if (hash && hash !== last.name && hash !== 'battle' && ['menu', 'notebook', 'creator', 'help', 'multiplayer'].includes(hash)) return null;
    return { name: last.name as SceneName, params: last.params };
  }

  private initialRoute(): SceneName {
    const name = location.hash.replace('#/', '');
    if (name === 'notebook' || name === 'creator' || name === 'help' || name === 'multiplayer') return name;
    return 'menu';
  }

  /**
   * Battles run at the chosen frame rate; the calm pages (menu, notebook,
   * creator…) only animate idle doodles, so 30 frames a second is plenty and
   * halves the drawing work there.
   */
  applyFrameRate(): void {
    this.loop.setFpsCap(this.scenes.currentName === 'battle' ? this.store.state.settings.fpsCap : 30);
  }

  private applySettings(): void {
    const s = this.store.state.settings;
    motion.setOverride(s.reducedMotion === 'auto' ? null : s.reducedMotion === 'on');
    this.sfx.setVolume(s.volume, s.muted);
    this.music.setVolume(Number.isFinite(s.music) ? s.music : 0.5, s.muted);
    this.applyFrameRate();
    this.debug.setVisible(s.debug || new URLSearchParams(location.search).has('debug'));
    this.viewport.setDprCap(s.dprCap);
  }

  /** A friend's battle invite: a sticky note with Join / Not now until it expires. */
  private showInvite(inv: InviteView): void {
    this.overlay.querySelector(`.invite-card[data-from="${CSS.escape(inv.fromId)}"]`)?.remove();
    this.sfx.play('pop');
    const close = (): void => {
      window.clearTimeout(timer);
      card.remove();
    };
    const card = h(
      'div',
      { class: 'invite-card sticky-note pink', role: 'alertdialog', 'aria-label': t('Battle invite from {name}', { name: inv.fromName }), 'data-from': inv.fromId },
      h('p', null, h('b', null, inv.fromName), ` ${t('invites you to a battle!')}`),
      h(
        'div',
        { class: 'row-actions' },
        button(t('Join!'), {
          variant: 'primary',
          color: 'yellow',
          small: true,
          sfx: this,
          onClick: () => {
            close();
            void this.scenes.go('multiplayer', { acceptInvite: inv });
          },
        }),
        button(t('Not now'), {
          variant: 'ghost',
          small: true,
          sfx: this,
          onClick: () => {
            close();
            this.mp.send({ t: 'invite.respond', fromId: inv.fromId, accept: false }).catch((e: ErrorMsg) => this.toast(e.message, 'info'));
          },
        }),
      ),
    );
    // Count the lifetime from now: the server's expiresAt is on the server's clock.
    const timer = window.setTimeout(() => card.remove(), INVITE_TTL_MS);
    this.overlay.appendChild(card);
  }

  toast(message: string, kind: 'info' | 'error' | 'success' = 'info'): void {
    const el = h('div', { class: `toast sticky-note ${kind}`, role: 'status' }, message);
    this.overlay.appendChild(el);
    window.setTimeout(() => el.classList.add('leaving'), 3200);
    window.setTimeout(() => el.remove(), 3800);
  }
}
